import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';

import { FeatureFlags } from 'featureFlags/constants';
import { NotFound } from 'error/components/NotFound';
import RecipientLandingPage from 'gift-cards/components/recipientLandingPage/RecipientLandingPage';
import { requestService } from 'shared/utils/services/requestService';
import { setIsFooterVisible } from 'store/slice/pageLayoutSlice';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch } from 'store/hooks/storeHooks';

const GiftCardRecipientLandingPage = () => {
  const [giftCardDetails, setGiftCardDetails] = useState();
  const [giftCardCodeDetails, setGiftCardCodeDetails] = useState();
  const [giftCardSpace, setGiftCardSpace] = useState();
  const [giftCardGroup, setGiftCardGroup] = useState();
  const [showNotFound, setShowNotFound] = useState(false);
  const { query } = useRouter();
  const isMounted = useIsMounted();
  const { signal } = useAbortController()();
  const storeDispatch = useStoreDispatch();
  const isEmailEntryRequired = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_GIFT_CARDS_REQUIRE_EMAIL_ENTRY,
  );

  useEffect(() => {
    if (isMounted()) {
      storeDispatch(setIsFooterVisible(false));
    }
  }, []);

  useEffect(() => {
    const fetchGiftCard = async () => {
      try {
        const giftCardData = await requestService({
          url: `/web-api/v1/gift-cards/${query?.uuid as string}`,
          method: 'get',
          signal,
        });

        let giftCardCodeData = null;

        if (!isEmailEntryRequired) {
          giftCardCodeData = await requestService({
            url: `/web-api/v1/gift-cards/${query?.uuid as string}/code`,
            method: 'get',
            signal,
          });
        }

        const { spaceId } = giftCardData;
        const space = await requestService({
          url: `/web-api/v1/spaces/${spaceId as string}`,
          method: 'get',
          signal,
        });

        const group = await requestService({
          url: `/web-api/v1/spaces/groups/${space.data.groupId as string}`,
          method: 'get',
          signal,
        });

        if (isMounted()) {
          setGiftCardDetails(giftCardData);
          setGiftCardCodeDetails(giftCardCodeData);
          setGiftCardSpace(space.data);
          setGiftCardGroup(group.dataList[0]);
        }
      } catch (_error: any) {
        if (isMounted()) {
          setShowNotFound(true);
        }
        Bugsnag.notify(_error, (event) => {
          event.severity = 'error';
          event.context = 'recipientLandingPage';
          event.addMetadata('recipientLandingPage', { giftCardId: query?.uuid });
        });
      }
    };

    if (query?.uuid) {
      void fetchGiftCard();
    }
  }, [query?.uuid]);

  useEffect(() => {
    if (showNotFound && isMounted()) {
      storeDispatch(setIsFooterVisible(true));
    }
  }, [showNotFound]);

  // RLP stands for Recipient Landing Page
  const requiredChecksToRenderRLP = () => {
    const defaultChecks = [giftCardDetails, giftCardSpace, giftCardGroup];

    if (!isEmailEntryRequired) {
      defaultChecks.push(giftCardCodeDetails);
    }
    return defaultChecks.some((requiredCheck) => !requiredCheck);
  };

  if (showNotFound) {
    return <NotFound darkTheme />;
  }

  if (requiredChecksToRenderRLP()) {
    return null;
  }

  return (
    <RecipientLandingPage
      giftCardSpace={giftCardSpace}
      giftCardDetails={giftCardDetails}
      giftCardCodeDetails={giftCardCodeDetails}
      giftCardGroup={giftCardGroup}
      isEmailEntryRequired={isEmailEntryRequired}
    />
  );
};

export default GiftCardRecipientLandingPage;
